import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { useId, useState } from "react";

import { missingOtherPartyFields, otherPartyDetail, type OtherPartyRequest } from "@ravnar/api-contract";

import { MissingDetails } from "../-components/missing-details";
import { NoAccess } from "../-components/no-access";
import { OtherPartyForm } from "../-components/other-party-form";
import { PartyDocuments } from "../-components/party-documents";
import { ApiError } from "../../../api/client";
import { otherPartyQueryOptions, removeOtherParty, updateOtherParty } from "../../../api/other-parties";
import { useAskAgain } from "../../../api/refresh";
import { sessionQueryOptions, useSession } from "../../../api/session";
import { PageHeader } from "../../../components/shell/app-shell";
import { Button } from "../../../components/ui/button";
import { buttonClassName } from "../../../components/ui/button-styles";
import { Card, CardHeader } from "../../../components/ui/card";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog";
import { showToast } from "../../../lib/toast";
import { DIRECTORY_DESCRIPTION } from "../index";

export const Route = createFileRoute("/directory/other/$partyId")({
	loader: async ({ context, params }) => {
		// A member with no access to the Directory is told so by the page; asking the API would only answer 403.
		const session = await context.queryClient.query(sessionQueryOptions());
		if (session === null || session.member.access.directory === "none") {
			return;
		}
		try {
			return await context.queryClient.query({
				...otherPartyQueryOptions(params.partyId),
				staleTime: "static",
			});
		} catch (error) {
			// Somebody removed it, or the id was typed by hand: the router's not-found page says so.
			if (error instanceof ApiError && error.status === 404) {
				notFound({ throw: true });
			}
			throw error;
		}
	},
	head: ({ loaderData }) => ({ meta: [{ title: `${loaderData?.name ?? "Party"} · Directory · Ravnar` }] }),
	staticData: { crumbs: ["Directory", "Party"] },
	component: OtherPartyPage,
});

interface OtherPartyDetailProps {
	/** Only an editor of the Directory may change the record, save the note, or remove the party. */
	readonly canEdit: boolean;
}

function OtherPartyDetail({ canEdit }: OtherPartyDetailProps) {
	const { partyId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const askAgain = useAskAgain();
	const { data: party } = useSuspenseQuery(otherPartyQueryOptions(partyId));
	const [isConfirmingRemoval, setIsConfirmingRemoval] = useState(false);
	const formId = useId();

	// A change to a party touches the kind's own queries and the audit log, which lives under "settings"; the
	// Directory that projects it and the entry the queue carries both land after the answer, so they are asked again.
	const refreshParty = () =>
		Promise.all([
			queryClient.invalidateQueries({ queryKey: ["other-parties"] }),
			queryClient.invalidateQueries({ queryKey: ["settings"] }),
			// Saving or removing a party closes the work item that asked for it, which the Work page and the rail count.
			queryClient.invalidateQueries({ queryKey: ["work"] }),
			askAgain(["directory"], ["settings", "audit-log"]),
		]);
	const saveMutation = useMutation({
		mutationFn: (input: OtherPartyRequest) => updateOtherParty(partyId, input),
		onSuccess: refreshParty,
	});
	const removeMutation = useMutation({ mutationFn: () => removeOtherParty(partyId), onSuccess: refreshParty });

	const saveParty = async (input: OtherPartyRequest) => {
		try {
			await saveMutation.mutateAsync(input);
		} catch {
			// The alert under the form shows saveMutation.error.
			return;
		}
		showToast(`Changes to ${input.name} saved.`);
	};

	const removePartyForGood = async () => {
		try {
			await removeMutation.mutateAsync();
		} catch {
			// The dialog's own error prop shows removeMutation.error, which is why it does not close itself.
			return;
		}
		setIsConfirmingRemoval(false);
		void navigate({ to: "/directory", search: {} });
		showToast(`${party.name} removed from the Directory.`);
	};

	return (
		<div className="max-w-[880px]">
			<PageHeader
				title={party.name}
				description={otherPartyDetail(party)}
				actions={
					<>
						<Link to="/directory" search={{}} className={buttonClassName({ size: "md" })}>
							Back to Directory
						</Link>
						{canEdit ? (
							<Button
								className="text-alert hover:text-alert"
								onClick={() => {
									setIsConfirmingRemoval(true);
								}}
							>
								Remove party
							</Button>
						) : null}
					</>
				}
			/>
			<MissingDetails missing={missingOtherPartyFields(party)} noun="party" />
			<Card>
				<CardHeader title="Party" />
				<div className="p-4">
					<OtherPartyForm key={party.id} formId={formId} party={party} canEdit={canEdit} onSave={saveParty} />
				</div>
			</Card>
			{saveMutation.error === null ? null : (
				<p role="alert" className="mt-3.5 text-caption text-alert">
					{saveMutation.error.message}
				</p>
			)}
			{canEdit ? (
				<div className="mt-3.5 flex items-center justify-end gap-2">
					<Button type="submit" form={formId} variant="primary" disabled={saveMutation.isPending}>
						{saveMutation.isPending ? "Saving…" : "Save changes"}
					</Button>
				</div>
			) : null}
			<PartyDocuments kind="other" partyId={party.id} name={party.name} />
			<ConfirmDialog
				open={isConfirmingRemoval}
				title={`Remove ${party.name}?`}
				confirmLabel="Remove party"
				busy={removeMutation.isPending}
				error={removeMutation.error?.message}
				onConfirm={() => {
					void removePartyForGood();
				}}
				onClose={() => {
					removeMutation.reset();
					setIsConfirmingRemoval(false);
				}}
			>
				Its record and its note are deleted for good. The audit log keeps that it happened.
			</ConfirmDialog>
		</div>
	);
}

function OtherPartyPage() {
	const { member } = useSession();
	return member.access.directory === "none" ? (
		<NoAccess description={DIRECTORY_DESCRIPTION} />
	) : (
		<OtherPartyDetail canEdit={member.access.directory === "editor"} />
	);
}
